import { MdlDialogReference } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component, Inject, InjectionToken, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { StudyGroupInfo, SampleTrackerData } from '../group-info/study-group-info';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest, merge, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { maxLengthInBytes, maxBytesError } from '../../shared/validators';
import { UserStorage, User } from '../../user';
import { SampleTrackingStorage } from '../sample-tracking-storage';
import { TextHighlightService } from '../service/text-highlight/text-highlight-service';
import { SampleTrackingDialogResult, SampleTrackingResultAction } from '../service/dialog-service/sample-tracking-result';
import { EditGroupRequest } from './edit-study-group-request';
import { ActivityLogger } from '../../activities/activity-logger';

export const STUDY_GROUP_EDIT_TOKEN = new InjectionToken('StudyGroupEditItem');

export const STUDY_GROUP_EDIT_INFO_TOKEN = new InjectionToken('StudyGroupEditInfoItem');


@Component({
    selector: 'edit-study-group',
    template: require('./edit-study-group.component.html'),
    styles: [require('./edit-study-group.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class EditStudyGroupComponent implements OnInit, OnDestroy {

    public createFromGroup: FormGroup;
    public searchTerm = new FormControl('');
    public studyGroupTitleCtl: FormControl;
    public studyListInfo: SampleTrackerData[] = [];

    public groupStudies: SampleTrackerData[] = [];

    public otherStudies: SampleTrackerData[] = [];

    private readonly titleMaxBytes = 75;
    private user: User;
    private subscription: Subscription;

    constructor(private dialog: MdlDialogReference,
                private sampleTrackingStorage: SampleTrackingStorage,
                private userStorage: UserStorage,
                private textHighlighterService: TextHighlightService,
                private readonly changeDetectorRef: ChangeDetectorRef,
                activityLogger: ActivityLogger,
                @Inject(STUDY_GROUP_EDIT_TOKEN) private readonly studyGroups: StudyGroupInfo[],
                @Inject(STUDY_GROUP_EDIT_INFO_TOKEN) private readonly studyGroupInfo: StudyGroupInfo) {

        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );

        this.studyGroupTitleCtl = new FormControl(studyGroupInfo.groupName, [Validators.required, maxLengthInBytes(this.titleMaxBytes)]);

        const data$ = this.sampleTrackingStorage.loadStudies();

        this.subscription = combineLatest([
            data$,
            searchTermFlow
        ]).pipe(
            map(([studies, term]: [SampleTrackerData[], string]) =>
                [this.createOtherStudies(studies), term]
            ),
            map(([studies, term]: [[SampleTrackerData[], SampleTrackerData[]], string]) =>
                [this.applyQuickSearchFilter(studies, term)]
            ))
            .subscribe(([studies]: [[SampleTrackerData[], SampleTrackerData[]]]) => {
                this.groupStudies = studies[0];
                this.otherStudies = studies[1];
                this.changeDetectorRef.markForCheck();

            });

        activityLogger.logActivity('Edit Study Group - Sample Tracking');
    }

    public async ngOnInit() {
        this.user = await this.userStorage.currentUser
            .pipe(
                map(u => u))
            .toPromise();

    }

    public ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    public close() {
        const result: SampleTrackingDialogResult = {
            action: SampleTrackingResultAction.close,
        };

        this.dialog.hide(result);
    }

    public get titleErrorMessage(): string {
        if (this.studyGroupTitleCtl.hasError('required')) {
            return 'Group Name is required';
        }

        if (this.studyGroupTitleCtl.hasError(maxBytesError)) {
            return `Title is too long`;
        }

        if (!this.isUniqueName) {
            return `Group name already exists`;
        }

        return '';
    }

    public get titleLabel(): string {
        if (!this.studyGroupTitleCtl.value) {
            return 'Type a Unique Study Grouping name here';
        }

        return '';
    }

    public get isFormInValid() {
        return this.studyGroupTitleCtl.invalid || !this.isUniqueName;
    }

    public editGroup() {

        const selectedOtherStudies = this.getSelectedOtherStudies();
        const unCheckedGroupStudies = this.getUnCheckedGroupStudies();
        const groupName = this.studyGroupTitleCtl.value.trim();

        const editrequest = new EditGroupRequest(
            this.user.metadata.userId,
            this.studyGroupInfo.groupPk,
            groupName,
            selectedOtherStudies,
            unCheckedGroupStudies
        );

        const result: SampleTrackingDialogResult = {
            action: SampleTrackingResultAction.edit,
            editRequest: editrequest
        };

        return this.dialog.hide(result);

    }


    public get isErrorExists() {
        const selectedOtherStudies = this.getSelectedOtherStudies();
        const unCheckedGroupStudies = this.getUnCheckedGroupStudies();

        return !this.oneStudyChecked(selectedOtherStudies, unCheckedGroupStudies);
    }

    private get isSaveDisabled() {
        const selectedOtherStudies = this.getSelectedOtherStudies();
        const unCheckedGroupStudies = this.getUnCheckedGroupStudies();
        const groupName = this.studyGroupTitleCtl.value.trim();

        if (!this.isGroupNameUpdated(groupName) &&
            !this.isStudyUpdated(selectedOtherStudies, unCheckedGroupStudies)) {
            return true;
        }

        if (!this.oneStudyChecked(selectedOtherStudies, unCheckedGroupStudies)) { return true; }
        if (this.isFormInValid) { return true; }

        const isUpdated = this.isGroupNameUpdated(groupName) || this.oneStudyChecked(selectedOtherStudies, unCheckedGroupStudies);

        if (!isUpdated) {
            return true;
        }

        return false;
    }

    private getSelectedOtherStudies(): number[] {
        return this.otherStudies.filter(study => study.isChecked).map((study) => study.protocolFk);
    }

    private getUnCheckedGroupStudies(): number[] {
        return this.groupStudies.filter(study => !study.isChecked).map((study) => study.protocolFk);
    }

    private applyQuickSearchFilter(data: [SampleTrackerData[], SampleTrackerData[]], term: string) {
        for (var info of data) {

            info.forEach(study => study.cardProtocolCode = this.textHighlighterService.
                getHighlightedText(study.protocolCode, term));
        }
        return data;
    }

    private createOtherStudies(studies: SampleTrackerData[]) {
        const groupStudyList: SampleTrackerData[] = [];
        const otherStudyList: SampleTrackerData[] = [];
        for (var study of studies) {
            const filteredResult = this.studyGroupInfo.studyList.filter(info =>
                info.protocolCode.toLowerCase() === study.protocolCode.toLowerCase());

            if (filteredResult && filteredResult.length > 0) {
                study.isChecked = true;
                groupStudyList.push(study);
                continue;
            }
            otherStudyList.push(study);
        }

        return [groupStudyList, otherStudyList];

    }

    private get isUniqueName(): boolean {
        var value = this.studyGroupTitleCtl.value;
        if (!value) { return true; }
        value = value.trim();
        if (value.toLowerCase() === this.studyGroupInfo.groupName.trim().toLowerCase()) { return true; }
        const filteredStudies = this.studyGroups.filter(info => info.groupName.trim().toLowerCase() === value.toLowerCase());

        return filteredStudies.length > 0 ? false : true;
    }

    private isGroupNameUpdated(groupName: string): boolean {
        if (this.isFormInValid || groupName.toLowerCase() === this.studyGroupInfo.groupName.trim().toLowerCase()) {
            return false;
        }
        return true;
    }

    private isStudyUpdated(selectedOtherStudies: number[], unCheckedGroupStudies: number[]) {
        if ((!selectedOtherStudies || selectedOtherStudies.length === 0) &&
            (!unCheckedGroupStudies || unCheckedGroupStudies.length === 0)) {
            return false;
        }

        return true;
    }

    private oneStudyChecked(selectedOtherStudies: number[], unCheckedGroupStudies: number[]): boolean {
        if (selectedOtherStudies && selectedOtherStudies.length > 0) {
            return true;
        }
        if (unCheckedGroupStudies && unCheckedGroupStudies.length !== this.studyGroupInfo.studyList.length) {
            return true;
        }
        return false;

    }

}